import React, { useState, useEffect, useCallback } from "react";
import {
    Bell, Camera, Settings, Search, Filter, ChevronLeft, ChevronRight,
    User, Users, CheckCircle, X, AlertCircle, Ban, UserX
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import NotificationModal from "../FORM MANAGEMENT/modals/NotificationModal";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getAllUsers } from "../../redux/Actions/AdminAction";
import { UpdateUserProfile } from "../../redux/Actions/AuthAction";
import { RoleEnum } from "../../modules/shared/model";
import { GlobalService } from "../../modules";

interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: { id: number; name: string };
    status: { id: number; name: string };
    cameraPermission?: boolean;
    enableNotification?: boolean;
    createdAt: string;
    updatedAt: string;
}

interface PaginationInfo {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNextPage: boolean;
    itemsPerPage: number;
}

interface MarketingPopupsNotificationsProps {
    searchQuery: string;
    onSearchChange: (query: string) => void;
}

const MarketingPopupsNotifications: React.FC<MarketingPopupsNotificationsProps> = ({
    searchQuery,
    onSearchChange
}) => {
    const dispatch = useAppDispatch();
    const { allUsers: adminUsers, loading: usersLoading } = useAppSelector((state: any) => state.admin);

    // Local state
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [showPermissionModal, setShowPermissionModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        hasNextPage: false,
        itemsPerPage: 10
    });

    // Load users
    const loadUsers = useCallback(async () => {
        try {
            await dispatch(getAllUsers({
                page: currentPage,
                limit: itemsPerPage,
                roles: [{ id: RoleEnum.professional }, { id: RoleEnum.internationl_professional }],
                search: searchQuery,
                sortBy: "createdAt",
                sortOrder: "desc"
            })).unwrap();
        } catch (error) {
            console.error("Failed to load users:", error);
            GlobalService.showToast("error", "Error", "Failed to load users");
        }
    }, [dispatch, currentPage, itemsPerPage, searchQuery]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    // Update pagination info when Redux state changes
    useEffect(() => {
        if (adminUsers) {
            setPaginationInfo({
                currentPage: adminUsers.currentPage || currentPage,
                totalPages: adminUsers.totalPages || 1,
                totalCount: adminUsers.totalCount || 0,
                hasNextPage: adminUsers.hasNextPage || false,
                itemsPerPage: itemsPerPage
            });
        }
    }, [adminUsers, currentPage, itemsPerPage]);

    // Handle search with debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (currentPage !== 1) {
                setCurrentPage(1);
            } else {
                loadUsers();
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

     // Handle permission update (for individual updates if needed)
     const handlePermissionUpdate = async (userId: string, permissionType: "camera" | "notifications", value: boolean) => {
         try {
             const updateData = {
                 cameraPermission: permissionType === "camera" ? value : undefined,
                 enableNotification: permissionType === "notifications" ? value : undefined,
                 photo: null
             };

             // Remove undefined values
             const cleanUpdateData = Object.fromEntries(
                 Object.entries(updateData).filter(([_, value]) => value !== undefined)
             );

             await dispatch(UpdateUserProfile({ id: userId, data: cleanUpdateData })).unwrap();
             GlobalService.showToast("success", "Success", "Permissions updated successfully");

             // Reload users to get updated data
             loadUsers();
         } catch (error: any) {
             console.error("Failed to update permissions:", error);
             GlobalService.showToast("error", "Error", error.message || "Failed to update permissions");
         }
     };

    // Handle notification send
    const handleSendNotification = (user: User) => {
        setSelectedUser(user);
        setShowNotificationModal(true);
    };

    // Handle permission management
    const handleManagePermissions = (user: User) => {
        setSelectedUser(user);
        setShowPermissionModal(true);
    };

    // Pagination handlers
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (paginationInfo.hasNextPage) {
            setCurrentPage(currentPage + 1);
        }
    };

     // User Card Component
     const UserCard = ({ user }: { user: User }) => (
         <Card className="p-4 sm:p-5 md:p-6 hover:shadow-lg transition-all duration-200 border border-gray-200 hover:border-blue-300" style={{ textAlign: "left" }}>
             {/* Header Section */}
             <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4 mb-6">
                 {/* User Info */}
                 <div className="flex-1 min-w-0">
                     <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
                         <h3 className="font-semibold text-lg sm:text-xl text-gray-900 truncate">
                             {user.firstName} {user.lastName}
                         </h3>
                     </div>
                     
                     <div className="space-y-1">
                         <div className="flex flex-wrap gap-2 mb-2">
                             <Badge
                                 variant="outline"
                                 className={`text-xs px-2 py-1 ${
                                     Number(user.role.id) === RoleEnum.professional
                                         ? "bg-blue-100 text-blue-700 border-blue-200"
                                         : "bg-green-100 text-green-700 border-green-200"
                                 }`}
                             >
                                 {user.role.name}
                             </Badge>
                             <Badge
                                 variant={Number(user.status.id) === 1 ? "success" : "destructive"}
                                 className="text-xs px-2 py-1"
                             >
                                 {Number(user.status.id) === 1 ? "Active" : "Inactive"}
                             </Badge>
                         </div>
                         <p className="text-sm text-gray-600 truncate">{user.email}</p>
                         <p className="text-xs text-gray-500">
                             Joined: {new Date(user.createdAt).toLocaleDateString()}
                         </p>
                     </div>
                 </div>

                 {/* Action Buttons */}
                 <div className="flex flex-col sm:flex-row gap-2 lg:flex-col xl:flex-row">
                     <Button
                         variant="outline"
                         size="sm"
                         className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium min-w-[100px]"
                         onClick={() => handleSendNotification(user)}
                     >
                         <Bell className="h-4 w-4" />
                         <span className="hidden sm:inline">Notify</span>
                     </Button>
                     <Button
                         variant="outline"
                         size="sm"
                         className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium min-w-[100px]"
                         onClick={() => handleManagePermissions(user)}
                     >
                         <Settings className="h-4 w-4" />
                         <span className="hidden sm:inline">Permissions</span>
                     </Button>
                 </div>
             </div>

             {/* Permissions Section */}
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                 <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-100">
                     <div className="flex items-center justify-between">
                         <div className="flex items-center gap-2 min-w-0">
                             <Camera className="h-4 w-4 text-gray-500 flex-shrink-0" />
                             <span className="text-sm font-medium text-gray-700">Camera Access</span>
                         </div>
                         <Badge
                             variant="outline"
                             className={`text-xs px-2 py-1 ${
                                 user.cameraPermission 
                                     ? "bg-green-100 text-green-700 border-green-200" 
                                     : "bg-red-100 text-red-700 border-red-200"
                             }`}
                         >
                             {user.cameraPermission ? "Enabled" : "Disabled"}
                         </Badge>
                     </div>
                 </div>
                 
                 <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-100">
                     <div className="flex items-center justify-between">
                         <div className="flex items-center gap-2 min-w-0">
                             <Bell className="h-4 w-4 text-gray-500 flex-shrink-0" />
                             <span className="text-sm font-medium text-gray-700">Notifications</span>
                         </div>
                         <Badge
                             variant="outline"
                             className={`text-xs px-2 py-1 ${
                                 user.enableNotification 
                                     ? "bg-green-100 text-green-700 border-green-200" 
                                     : "bg-red-100 text-red-700 border-red-200"
                             }`}
                         >
                             {user.enableNotification ? "Enabled" : "Disabled"}
                         </Badge>
                     </div>
                 </div>
             </div>
         </Card>
     );

     // Permission Modal Component
     const PermissionModal = ({ isOpen, onClose, user }: { isOpen: boolean; onClose: () => void; user: User | null }) => {
         const [cameraAccess, setCameraAccess] = useState(user?.cameraPermission || false);
         const [notificationAccess, setNotificationAccess] = useState(user?.enableNotification || false);
         const [isUpdating, setIsUpdating] = useState(false);

        if (!isOpen || !user) return null;

         const handleSave = async () => {
             setIsUpdating(true);
             try {
                 // Update both permissions in a single API call with backend-expected format
                 const updateData = {
                     cameraPermission: cameraAccess,
                     enableNotification: notificationAccess,
                     photo: (user as any).photo || null,
                     cover: (user as any).cover || null
                 };

                 await dispatch(UpdateUserProfile({ id: user.id, data: updateData })).unwrap();
                 GlobalService.showToast("success", "Success", "Permissions updated successfully");

                 // Reload users to get updated data
                 loadUsers();
                 onClose();
             } catch (error: any) {
                 console.error("Failed to update permissions:", error);
                 GlobalService.showToast("error", "Error", error.message || "Failed to update permissions");
             } finally {
                 setIsUpdating(false);
             }
         };

        return (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" style={{ textAlign: "left" }}>
                <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                    <div className="p-6 border-b sticky top-0 bg-white z-10">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-semibold">Manage Permissions for {user.firstName} {user.lastName}</h2>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    <div className="p-6">
                        <div className="space-y-6">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                                    <p className="text-sm text-blue-700">
                                        Changing permissions will affect this user&apos;s ability to interact with certain features of the platform.
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                 <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                     <div className="flex items-center gap-2">
                                         <Camera className="h-5 w-5 text-gray-700" />
                                         <div>
                                             <p className="font-medium">Camera Access</p>
                                             <p className="text-sm text-gray-500">Allow the user to use video call features</p>
                                         </div>
                                     </div>
                                     <label className="flex items-center cursor-pointer">
                                         <input
                                             type="checkbox"
                                             className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                             checked={cameraAccess}
                                             onChange={(e) => setCameraAccess(e.target.checked)}
                                         />
                                         <span className="ml-2 text-sm text-gray-700">
                                             {cameraAccess ? "Enabled" : "Disabled"}
                                         </span>
                                     </label>
                                 </div>

                                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <Bell className="h-5 w-5 text-gray-700" />
                                        <div>
                                            <p className="font-medium">Notification Access</p>
                                            <p className="text-sm text-gray-500">Allow the user to receive notifications</p>
                                        </div>
                                    </div>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                            checked={notificationAccess}
                                            onChange={(e) => setNotificationAccess(e.target.checked)}
                                        />
                                        <span className="ml-2 text-sm text-gray-700">
                                            {notificationAccess ? "Enabled" : "Disabled"}
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div className="pt-4 border-t flex justify-end gap-3">
                                <Button variant="outline" onClick={onClose} disabled={isUpdating}>
                                    Cancel
                                </Button>
                                <Button onClick={handleSave} disabled={isUpdating}>
                                    {isUpdating ? "Saving..." : "Save Changes"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Pagination Component
    const Pagination = () => {
        const pages = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(paginationInfo.totalPages, startPage + maxVisiblePages - 1);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`px-3 py-2 text-sm rounded-lg ${currentPage === i
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                >
                    {i}
                </button>
            );
        }

        return (
            <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, paginationInfo.totalCount)} of {paginationInfo.totalCount} users
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    {pages}
                    <button
                        onClick={handleNextPage}
                        disabled={!paginationInfo.hasNextPage}
                        className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6">
             {/* Stats Cards for Notifications */}
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <Card className="p-4 md:p-6">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-sm text-gray-500">Total Users</p>
                            <p className="text-2xl font-semibold mt-1">{paginationInfo.totalCount}</p>
                        </div>
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Users className="h-5 w-5 text-blue-600" />
                        </div>
                    </div>
                </Card>

                <Card className="p-4 md:p-6">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-sm text-gray-500">Active Users</p>
                             <p className="text-2xl font-semibold mt-1">
                                 {adminUsers?.data?.filter((u: User) => Number(u.status.id) === 1).length || 0}
                             </p>
                        </div>
                        <div className="p-2 bg-green-100 rounded-lg">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                    </div>
                </Card>

                <Card className="p-4 md:p-6">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-sm text-gray-500">Camera Enabled</p>
                 <p className="text-2xl font-semibold mt-1">
                     {adminUsers?.data?.filter((u: User) => u.cameraPermission).length || 0}
                 </p>
                        </div>
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <Camera className="h-5 w-5 text-purple-600" />
                        </div>
                    </div>
                </Card>

                <Card className="p-4 md:p-6">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-sm text-gray-500">Notifications Enabled</p>
                 <p className="text-2xl font-semibold mt-1">
                     {adminUsers?.data?.filter((u: User) => u.enableNotification).length || 0}
                 </p>
                        </div>
                        <div className="p-2 bg-yellow-100 rounded-lg">
                            <Bell className="h-5 w-5 text-yellow-600" />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Users List */}
            {usersLoading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading users...</p>
                    </div>
                </div>
            ) : adminUsers?.data?.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                        <div className="text-gray-400 mb-4">
                            <User className="h-12 w-12 mx-auto" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                        <p className="text-gray-600">
                            {searchQuery ? "Try adjusting your search criteria" : "No professional users found"}
                        </p>
                    </div>
                </div>
             ) : (
                 <>
                     <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                         {adminUsers?.data?.map((user: User) => (
                             <UserCard key={user.id} user={user} />
                         ))}
                     </div>
                     <Pagination />
                 </>
             )}

            {/* Notification Modal */}
            {showNotificationModal && selectedUser && (
                <NotificationModal
                    isOpen={true}
                    onClose={() => setShowNotificationModal(false)}
                    client={selectedUser}
                    roles={[{ id: RoleEnum.professional }, { id: RoleEnum.internationl_professional }]}
                />
            )}

            {/* Permission Modal */}
            {showPermissionModal && selectedUser && (
                <PermissionModal
                    isOpen={true}
                    onClose={() => setShowPermissionModal(false)}
                    user={selectedUser}
                />
            )}
        </div>
    );
};

export default MarketingPopupsNotifications;
